import React, { useMemo } from "react";
import { isStartListStatus, isLiveStatus } from "helpers/status";
import LoaderSubComponent from "components/general/LoaderSubComponent";
import MirsTable from "components/general/table/Table";
import CaptionTable from "components/general/table/CaptionTable";
//General
import Status from "components/presentation/Status";
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";
import { getIsRoadRace, getIsTimeTrial } from 'components/sports/crd/helper/CrdHelpers'
import PhotoFinish from "components/general/PhotoFinish";
import Label from "components/general/Label";
import NotAvailable from "components/common/NotAvailable";
//Road Race
import { TableColumns as ColumnsRoadRaceStart, TableColumnsSM as ColumnsRoadRaceStartSM } from 'components/sports/crd/events/road_race/start/Columns'
import {
    TableColumnsLive as ColumnsRoadRaceResLive,
    TableColumnsLiveSM as ColumnsRoadRaceResLiveSM,
    TableColumnsFinish as ColumnsRoadRaceRes,
    TableColumnsFinishSM as ColumnsRoadRaceResSM
} from 'components/sports/crd/events/road_race/results/Columns'
import { TableExpanded as TBERoadRace } from 'components/sports/crd/events/road_race/results/TableExpanded'

//Time Trial
import { TableColumns as ColumnsTimeTrialStart, TableColumnsSM as ColumnsTimeTrialStartSM } from 'components/sports/crd/events/time_trial/start/Columns'
import {
    TableColumnsLive as ColumnsTimeTrialResLive,
    TableColumnsLiveSM as ColumnsTimeTrialResLiveSM,
    TableColumnsFinish as ColumnsTimeTrialRes,
    TableColumnsFinishSM as ColumnsTimeTrialResSM
} from 'components/sports/crd/events/time_trial/results/Columns'
import { TableExpanded as TBETimeTrial } from 'components/sports/crd/events/time_trial/results/TableExpanded'


//Team
import { TableColumns as ColumnsTeamsStart, TableColumnsSM as ColumnsTeamsStartSM } from 'components/sports/crd/events/teams/start/Columns'
import {
    TableColumnsFinish as ColumnsTeamsRes,
    TableColumnsFinishSM as ColumnsTeamsResSM
} from 'components/sports/crd/events/teams/results/Columns'
import { TableExpanded as TBETeams } from 'components/sports/crd/events/teams/results/TableExpanded'




import { hasConfig } from "components/sports/crd/helper/CrdHelpers"
//import TableMin from "components/table/TableMin";
import { useViewport } from 'components/general/useViewPort'


//Race Info
import CrdRaceInfo from 'components/sports/crd/views/CrdRaceInfo'



const getConfiguration = (data, rsc, resultStatus, breakPoint, caption) => {

    //determinar si la configuacion viene del config o del result depende del deporte
    const columnsAdded = data?.config?.extendedConfig ? data.config.extendedConfig.sort((a, b) => a.pos - b.pos) : []

    const dataAdditional = data?.results?.extendedInfos
    const isStartList = isStartListStatus(resultStatus) ? true : false
    const isLive = isLiveStatus(resultStatus) ? true : false
    const isRoadRace = getIsRoadRace(rsc);
    const isTimeTrial = getIsTimeTrial(rsc);


    let configuration = {
        caption: null,
        columns: [],
        //subComponent: null,
        hiddenColumns: [],
        sortBy: [],
        filters: {
            ei_type: ['NONE'],
            ei_code: ['NONE']
        },
        configs: {
            isStartList: isStartList,
            columnsAdded: columnsAdded,
            previousCurrent: false,
            dataAdditional: dataAdditional
        }
    };



    configuration.caption = <CaptionTable
        leftCol={<>{caption}</>}
        rightCol={<Status typeToPrint='result' scheduleStatus={data?.results?.schedule?.status?.code} resultStatus={resultStatus} />}
    />

    configuration.sortBy = isStartList
        ? [{ id: 'Bib', desc: false }]
        : isLive
            //is live
            ? (isRoadRace)
                ? [{ id: 'RankIntermediate', desc: false }]
                : [{ id: 'Rank', desc: false }] //TimeTrial or Teams
            //Finish
            : (isRoadRace)
                ? [{ id: 'Rank', desc: false }]
                : [{ id: 'Rank', desc: false }]//TimeTrial or Teams 


    configuration.filters = {
        ei_type: ['DISPLAY'],
        ei_code: ['INTERMEDIATE_CURRENT']
    };



    if (isRoadRace) {
        configuration.columns = isStartList
            ? breakPoint === "sm" || breakPoint === "xs"
                ? ColumnsRoadRaceStartSM()
                : ColumnsRoadRaceStart()
            : isLive
                ? breakPoint === "sm" || breakPoint === "xs"
                    ? ColumnsRoadRaceResLiveSM(columnsAdded)
                    : ColumnsRoadRaceResLive(columnsAdded)
                : breakPoint === "sm" || breakPoint === "xs"
                    ? ColumnsRoadRaceResSM(columnsAdded)
                    : ColumnsRoadRaceRes(columnsAdded);
    } else if (isTimeTrial) {
        //Time Trial
        configuration.columns = isStartList
            ? breakPoint === "sm" || breakPoint === "xs"
                ? ColumnsTimeTrialStartSM()
                : ColumnsTimeTrialStart()
            : isLive
                ? breakPoint === "sm" || breakPoint === "xs"
                    ? ColumnsTimeTrialResLiveSM(columnsAdded, dataAdditional)
                    : ColumnsTimeTrialResLive(columnsAdded, dataAdditional)
                : breakPoint === "sm" || breakPoint === "xs"
                    ? ColumnsTimeTrialResSM(columnsAdded)
                    : ColumnsTimeTrialRes(columnsAdded);

    } else {
        configuration.columns = isStartList
            ? breakPoint === "sm" || breakPoint === "xs"
                ? ColumnsTeamsStartSM()
                : ColumnsTeamsStart()
            : breakPoint === "sm" || breakPoint === "xs"
                ? ColumnsTeamsResSM(columnsAdded)
                : ColumnsTeamsRes(columnsAdded);
    }


    return configuration;
};


const CrdResults = ({ data, caption }) => {
    const { mirs_rsc } = useUrlFriendlyToParameters()
    const { breakPoint } = useViewport();
    const resultStatus = data?.results?.status?.code;



    //Cuando cambimos de estado de resultados forzamos a repintar la tabla con las nuevas columnas 
    //aunque no hay cambios    
    // eslint-disable-next-line
    const configEvent = useMemo(() => getConfiguration(data, mirs_rsc, resultStatus, breakPoint, caption), [resultStatus, mirs_rsc, breakPoint]);
    // Create a function that will render our row sub components
    const renderRowSubComponent = React.useCallback(({ row }) => {
        let componentExpanded = '';
        if (getIsRoadRace(mirs_rsc)) {
            componentExpanded = <TBERoadRace row={row?.original} config={configEvent.configs.columnsAdded} />
        } else if (getIsTimeTrial(mirs_rsc)) {
            componentExpanded = < TBETimeTrial row={row?.original} config={configEvent.configs.columnsAdded} />
        } else {
            componentExpanded = < TBETeams row={row?.original} config={configEvent.configs.columnsAdded} />
        }
        return (<LoaderSubComponent component={componentExpanded} />)
        // eslint-disable-next-line
    }, [resultStatus]);

    const items = data?.results?.items
    const showAllExtendedResults = items?.some(item => item?.extendedResults?.some(item => item.er_code === 'INTERMEDIATE'));
    const showAll = items?.some(item => item?.extendedResults?.some(item => item.er_code === 'INTERMEDIATE'));
    const hasConfiguration = hasConfig(configEvent.configs.columnsAdded)
    //Pasamos el id de la columna a ocultar definido en la configuracón de
    let hiddenColumns = []
    if (!showAll && !hasConfiguration) hiddenColumns.push('Expander')

    return (
        <>
            {data?.results ? (
                <>
                    {data?.images && <div className='text-end'><strong><Label data='LabelPhotoFinish' />:&nbsp;</strong><PhotoFinish data={data?.images} /></div>}
                    {data?.results?.items ? (
                        < MirsTable
                            className='table-result'
                            //Dato to show by row
                            data={data.results.items}
                            //Columnas a visualizar 
                            columns={configEvent.columns}
                            //subcomponente a renderizar, extended
                            renderRowSubComponent={renderRowSubComponent}
                            //pageSizeCustom={data.items.length} //ponerlo por defecto en el componente                             
                            //Key by row necesary in order to no paint innecesary
                            keyRow='participant-code'
                            //Component caption
                            caption={configEvent.caption}
                            //Columns to hidde 
                            hiddenColumns={hiddenColumns}
                            //When show tble is sorted by 
                            initialSortBy={configEvent.sortBy}
                            //Show buttons move to splits 
                            showNavExtendedData={true}
                            id={`mirs-table-result-${mirs_rsc}`}
                            hasRelatedTimeline={true}
                        />
                    ) : ('')
                    }
                </>
            ) : (
                <NotAvailable />
            )
            }
            {data?.config ? (
                <CrdRaceInfo config={data.config} />
            ) : ""}
        </>
    );
};

export default CrdResults;
